import { logout, getIdToken, getAccessToken } from '@/auth';
import { isValidUrl, isValidCallbackUrl } from '@/rules';
import { createLogger } from '@/logger';

function redirect({ next, payload, state, token, errors }) {
  const encodedErrors = (errors && errors.length)
    ? encodeURIComponent(JSON.stringify(errors))
    : null;

  const params = [
    state && `state=${encodeURIComponent(state)}`,
    token && `token=${token}`,
    encodedErrors && `errors=${encodedErrors}`,
  ].filter(p => p).join('&');

  window.location = `${next}${payload === 'fragment' ? '#' : '?'}${params}`;
}

/**
 * Authorizes an external application by redirecting to a callback url `next`
 * with a token and an optional state parameter `state` in the query or fragment
 * part.
 *
 * The `responsetype` parameter must be either 'id-token' or 'access-token'.
 * The `payload` parameter must be either 'query' or 'fragment'.
 *
 * The `silent` parameter can be set to enable silent authorization, where also
 * errors result in a redirect to the callback url.
 *
 * Note: the error callback may be called multiple times with different errors,
 * but will not be called in silent mode.
 */
export function authorize({
  silent = false,
  next,
  state,
  payload,
  responsetype,
  error,
}) {
  const log = createLogger('redirect.authorize');
  const silentErrors = [];

  const pushError = err => (silent ? silentErrors.push(err) : error(err));

  let ready = true;

  if (!isValidCallbackUrl(next)) {
    log.error({
      message: `Authorize attempt with invalid callback url: "${next}".`,
    });

    if (next) {
      pushError('The requested application to authorize for is not allowed.');
    } else {
      pushError('No application to authorize for.');
    }
    ready = false;
  }

  if (payload !== 'query' && payload !== 'fragment') {
    log.error({
      message: `Authorize attempt with invalid payload type: "${payload}".`,
    });

    pushError('Invalid payload type requested');
    ready = false;
  }

  if (responsetype !== 'access-token' && responsetype !== 'id-token') {
    log.error({
      message: `Authorize attempt with invalid token type: "${responsetype}".`,
    });

    pushError('Invalid token type requested');
    ready = false;
  }

  if (!ready) {
    if (silent) {
      redirect({ next, payload, errors: silentErrors });
    }
    return;
  }

  const callOpts = {
    // token should be valid for at least 30 minutes
    minValidSeconds: 1800,
    error: (err) => {
      if (silent) {
        redirect({ next, payload, errors: [err] });
      } else {
        error(err);
      }
    },
    success: (token) => {
      log
        .trace({ message: `User authorized for callback: "${next}".` })
        .then(() => {
          redirect({ next, payload, token, state });
        });
    },
  };

  if (responsetype === 'access-token') getAccessToken(callOpts);
  else if (responsetype === 'id-token') getIdToken(callOpts);
}

/**
 * Logs out the current user and optionally redirects to a callback url `next`
 * with an optional state parameter `state` in the query part.
 *
 * Note: if no next url is given the `internalRedirect` callback is called.
 */
export function signout({ next, state, internalRedirect, error }) {
  const log = createLogger('redirect.signout');

  if (next && !isValidUrl(next)) {
    log.error({ message: `Invalid redirect url at signout: "${next}".` });
    error(`${next} is not an accepted callback url.`);
    return;
  }

  log.trace({ message: `Post-signout redirect: "${next}".` }).then(() => {
    logout();

    if (next) {
      redirect({ next, state });
    } else {
      internalRedirect();
    }
  });
}



// WEBPACK FOOTER //
// ./src/redirect/index.js